import { useState } from 'react';
import { ArrowLeftRight, TrendingUp, TrendingDown } from 'lucide-react';
import { LoopDetailsModal } from './LoopDetailsModal';

interface LoopPattern {
  fromPage: string;
  toPage: string;
  sessions: number;
  percentage: number;
}

interface LoopAnalysisProps {
  loops: LoopPattern[];
  totalSessions: number;
  companyId: string | null;
  dataSourceId: string | null;
  dateRange: {
    startDate: string;
    endDate: string;
  };
}

/**
 * LoopAnalysis Component
 * 
 * Shows back navigation patterns (cycles) that are hidden in the main Sankey diagram
 * due to d3-sankey's limitation with cyclic graphs.
 * 
 * This gives users insight into:
 * - Comparison shopping behavior (Product A ← → Product B)
 * - Browse-and-return patterns (Shop ← → Homepage)
 * - Multi-step processes (Form ← → Review ← → Confirmation)
 */
export const LoopAnalysis = ({ loops, totalSessions, companyId, dataSourceId, dateRange }: LoopAnalysisProps) => {
  const [selectedLoop, setSelectedLoop] = useState<LoopPattern | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleLoopClick = (loop: LoopPattern) => {
    setSelectedLoop(loop);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedLoop(null);
  };

  if (!loops || loops.length === 0) {
    return (
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <div className="flex items-center gap-2 mb-4">
          <ArrowLeftRight className="w-5 h-5 text-cyan-400" />
          <h3 className="text-lg font-semibold text-white">Loop Analysis</h3>
          <span className="text-xs text-gray-400 ml-auto">Back Navigation Patterns</span>
        </div>
        <p className="text-gray-400 text-sm">
          No significant back navigation patterns detected in this period.
        </p>
      </div>
    );
  }

  // Sort loops by session count
  const sortedLoops = [...loops].sort((a, b) => b.sessions - a.sessions);
  
  // Get top 10 loops
  const topLoops = sortedLoops.slice(0, 10);
  
  // Calculate total loop sessions
  const totalLoopSessions = loops.reduce((sum, loop) => sum + loop.sessions, 0);
  const loopPercentage = ((totalLoopSessions / totalSessions) * 100).toFixed(1);

  return (
    <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <ArrowLeftRight className="w-5 h-5 text-cyan-400" />
          <h3 className="text-lg font-semibold text-white">Loop Analysis</h3>
        </div>
        <div className="text-right">
          <div className="text-sm text-gray-400">Users with Back Navigation</div>
          <div className="text-2xl font-bold text-cyan-400">{loopPercentage}%</div>
          <div className="text-xs text-gray-500">
            {totalLoopSessions.toLocaleString()} of {totalSessions.toLocaleString()} sessions
          </div>
        </div>
      </div>

      <p className="text-sm text-gray-400 mb-4">
        These patterns show where users navigate back and forth between pages. Hidden in main Sankey
        due to d3-sankey's limitation with cycles.
      </p>

      <div className="space-y-3">
        {topLoops.map((loop, index) => {
          const isIncreasing = index === 0 || loop.sessions > topLoops[index - 1]?.sessions;
          
          return (
            <button
              key={`${loop.fromPage}-${loop.toPage}`}
              onClick={() => handleLoopClick(loop)}
              className="w-full bg-gray-750 rounded-lg p-4 border border-gray-700 hover:border-cyan-500 transition-colors cursor-pointer text-left"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-semibold text-gray-500">#{index + 1}</span>
                    {isIncreasing ? (
                      <TrendingUp className="w-4 h-4 text-green-400" />
                    ) : (
                      <TrendingDown className="w-4 h-4 text-gray-400" />
                    )}
                    <span className="text-xs text-cyan-400 ml-auto">Click for details →</span>
                  </div>
                  
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm text-white truncate">{shortenUrl(loop.fromPage)}</span>
                    <ArrowLeftRight className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                    <span className="text-sm text-white truncate">{shortenUrl(loop.toPage)}</span>
                  </div>
                  
                  <div className="text-xs text-gray-400 truncate" title={loop.fromPage}>
                    {loop.fromPage}
                  </div>
                </div>

                <div className="text-right flex-shrink-0">
                  <div className="text-lg font-bold text-cyan-400">
                    {loop.sessions.toLocaleString()}
                  </div>
                  <div className="text-xs text-gray-400">sessions</div>
                  <div className="text-xs text-gray-500">{loop.percentage.toFixed(1)}%</div>
                </div>
              </div>

              {/* Progress bar */}
              <div className="mt-3 bg-gray-800 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-cyan-500 to-blue-500 h-full rounded-full transition-all duration-500"
                  style={{ width: `${Math.min(loop.percentage, 100)}%` }}
                />
              </div>
            </button>
          );
        })}
      </div>

      {loops.length > 10 && (
        <div className="mt-4 text-center text-sm text-gray-400">
          Showing top 10 of {loops.length} loop patterns
        </div>
      )}

      {/* Loop Details Modal */}
      <LoopDetailsModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        loop={selectedLoop}
        companyId={companyId}
        dataSourceId={dataSourceId}
        dateRange={dateRange}
      />
    </div>
  );
};

// Helper function to shorten URLs for display
function shortenUrl(url: string): string {
  try {
    // Remove protocol and www
    let short = url.replace(/^https?:\/\/(www\.)?/, '');
    
    // Extract path without query params for display
    const pathMatch = short.match(/^[^?]+/);
    if (pathMatch) {
      short = pathMatch[0];
    }
    
    // Shorten long paths
    if (short.length > 40) {
      const parts = short.split('/');
      if (parts.length > 2) {
        return `${parts[0]}/.../${parts[parts.length - 1]}`;
      }
      return short.substring(0, 37) + '...';
    }
    
    return short;
  } catch (e) {
    return url.substring(0, 40) + (url.length > 40 ? '...' : '');
  }
}











